























import { isViewAsSelected } from "@/store/modules/TargetUserModule";
import { localiseSchedulingSettingsSection, SchedulingSettingsSection, } from "@/data/models/SchedulingSettingsSection";
import { MASTER_ID } from "@/data/models/User";
import router from "@/router/router";
import { formatUserRole, formatUserValidationLevel, } from "@/utils/LocalisationHelper";
import { Component, Ref, Vue } from "vue-property-decorator";
import SetNewPasswordComponent from "../users/SetNewPassword.vue";
import UserFormComponent from "../users/UserFormDialog.vue";
import ChangeEmailAndPasswordComponent from "./ChangeClientEmailAndPassword.vue";
import SettingsItemComponent, { SettingData } from "./SettingsItem.vue";
import {
  userSettingsAccess,
  companySettingsAccess,
  reportSettingsAccess,
  otherDeviceSettingsAccess,
  hyDryDeviceSettingsAccess,
  messageSettingsAccess,
  projectSettingsAccess,
  systemSettingsSectionAccess,
  timeTrackingSettingsAccess,
  schedulingSettingsAccess,
  iresSettingsAccess,
  usersConfigurationAccess,
  vehicleManagementAccess,
  roleManagementAccess,
  deviceSettingsItemAccess,
  importDevicesAccess,
  messageSelectionAccess,
  mailboxAccess,
  systemConfigurationAccess,
  adminRightsAccess,
  viewAdminsitrationSettingsAccess,
  viewSystemConfigurationDistributionAccess,
  systemSettingsAccess,
  viewArchivedProjectSettingsAccess,
  deviceManagerSettingsAccess,
  manageMemberClientAccess,
  viewFinishedProjectsSettingsAccess,
  branchManagementAccess,
  billingCatalogSettingsAccess,
} from "@/permissions/UserPermissions";
import {
  defineOwnRoleSettingsClientAccess,
  defineProjectManagementSettingsForGroupClientAccess,
  administratorClientSettingsAccess,
  defineOwnAllDevicesSettingsClientAccess,
  allDevicesSettingsClientAccess,
  isOfficeAdminPortalClient,
} from "@/permissions/ClientPermissions";
import { getProjectManagementType } from "@/data/repository/ClientRepository";
import { ProjectManagement } from "@/data/models/Client";
import MemberClientDialog from "../client/CreateClientDialog.vue";
import { getEffectiveUser } from "@/utils/ViewAs";

@Component({
  components: {
    "settings-item": SettingsItemComponent,
    "user-form": UserFormComponent,
    "set-password": SetNewPasswordComponent,
    "change-email-and-password": ChangeEmailAndPasswordComponent,
  },
})

export default class Settings extends Vue {
  @Ref("userForm")
  userFormComponent!: UserFormComponent;

  @Ref("setPassword")
  setNewPasswordComponent!: SetNewPasswordComponent;

  @Ref("memberClientDialog")
  memberClientDialog!: MemberClientDialog;

  /**
   * Whose settings page this is: the chosen user's while "Sicht auf" is active, matching the route
   * guard (ticket 2610052), the "Aktueller Benutzer" card included.
   */
  get effectiveUser() {
    return getEffectiveUser();
  }


  get activeUserDescription() {
    const activeUser = this.effectiveUser;

    if (activeUser === null) {
      return "";
    }

    const userRole = adminRightsAccess(activeUser) ?
      formatUserRole(this, activeUser.role) + "+Admin" :
      formatUserRole(this, activeUser.role);

    return this.$t("settings.currentUser.formatCurrentUser", [
      activeUser.nickname,
      userRole,
    ]).toString();
  }

  /**
   * The "Aktueller Benutzer" card: while "Sicht auf" is active it is the chosen user's, as they see it
   * (ticket 2610052; for a user with no other settings it is all the page has). Read-only then:
   * their profile opens without editing rights and the password entry is left out (ticket 2610051).
   */
  get showCurrentUserDetails() {
    return this.effectiveUser?.nickname !== "Master";
  }

  get showUserSettings() {
    const activeUser = this.effectiveUser;

    return userSettingsAccess(activeUser);
  }

  /**
   * Whether to hide the settings a restoration company needs but an administration tenant does not.
   *
   * The IRES admin portal manages devices, users and system configuration; it runs no projects
   * (verified in production: no projects, no project states, no reports) and bills nobody. Company
   * data, branches, reports, project management, time tracking, scheduling, billing catalogs and
   * member-client administration are therefore hidden for it.
   *
   * Deliberately does **not** hide user management: role management lives inside it, and that is
   * where the Drying Expert role and its reviewers are configured.
   *
   * Mirrored in `src/utils/RouteGuards.ts` so the pages cannot be reached by direct URL either.
   */
  get hidesClientBusinessSettings() {
    return isOfficeAdminPortalClient();
  }

  get showCompanySettings() {
    const activeUser = this.effectiveUser;

    return !this.hidesClientBusinessSettings && companySettingsAccess(activeUser);
  }

  get showReportSettings() {
    const activeUser = this.effectiveUser;

    return !this.hidesClientBusinessSettings && reportSettingsAccess(activeUser);
  }

  get showHyDryDeviceSettings() {
    const activeUser = this.effectiveUser;

    return hyDryDeviceSettingsAccess(activeUser);
  }

  get showOtherDevicesSettings() {
    const activeUser = this.effectiveUser;

    // Mirrors `areOtherDeviceSettingsComponentsAccessible` in RouteGuards.
    return allDevicesSettingsClientAccess() && (
      otherDeviceSettingsAccess(activeUser) ||
      defineOwnAllDevicesSettingsClientAccess()
    );
  }

  get showMessageSettings() {
    const activeUser = this.effectiveUser;

    return messageSettingsAccess(activeUser);
  }

  get showProjectSettings() {
    if (this.hidesClientBusinessSettings) {
      return false;
    }

    return this.projectSettingsVisibility ||
      this.projectManagerSettingsVisibility ||
      this.deviceManagerSettingsVisibility ||
      this.archivedProjectsVisibility ||
      this.administrationSettingsVisibility;
  }

  get hasProjectManagementRouteAccess() {
    const activeUser = this.effectiveUser;

    return projectSettingsAccess(activeUser) ||
      defineProjectManagementSettingsForGroupClientAccess();
  }

  get showSystemSettings() {
    const activeUser = this.effectiveUser;

    return systemSettingsSectionAccess(activeUser);
  }

  get showTimeTrackingSettings() {
    const activeUser = this.effectiveUser;

    return !this.hidesClientBusinessSettings && timeTrackingSettingsAccess(activeUser);
  }

  get showSchedulingSettings() {
    const activeUser = this.effectiveUser;

    return !this.hidesClientBusinessSettings && schedulingSettingsAccess(activeUser);
  }

  get isAllowedToManageMemberClients() {
    return manageMemberClientAccess(this.effectiveUser);
  }

  get showAdministratorClientSettings() {
    return !this.hidesClientBusinessSettings && administratorClientSettingsAccess() && (
      this.isAllowedToManageMemberClients
    );
  }

  get showIresSettings() {
    const activeUser = this.effectiveUser;

    return iresSettingsAccess(activeUser);
  }

  get showBillingCatalogSettings() {
    const activeUser = this.effectiveUser;

    return !this.hidesClientBusinessSettings && billingCatalogSettingsAccess(activeUser);
  }

  get currentUserSettingsSection(): SettingData {
    const activeUser = this.effectiveUser;

    if (activeUser === null) {
      return {
        title: "",
        items: []
      };
    }

    const showChangePasswordOption = activeUser && activeUser.id !== MASTER_ID && !isViewAsSelected();

    return {
      title: this.$t("settings.currentUser.title").toString(),
      items: [
        {
          title: this.activeUserDescription,
          icon: "mdi-account-edit",
          visible: true,
          allowMultiline: true,
          additionalText: this.$t("settings.currentUser.formatCurrentUserValidationLevel", [
            formatUserValidationLevel(this, activeUser.validationLevel)
          ]).toString(),
          onClickCallback: () => {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            this.userFormComponent.show(this.effectiveUser!!);
          },
        },
        ...(showChangePasswordOption
          ? [
            {
              title: this.$t("settings.currentUser.setPassword").toString(),
              icon: "mdi-account-key",
              visible: true,
              onClickCallback: () => {
                if (activeUser) {
                  this.setNewPasswordComponent.show(activeUser, true);
                }
              },
            },
          ]
          : []),
      ],
    };
  }

  get userSettingsVisibility() {
    const activeUser = this.effectiveUser;

    return usersConfigurationAccess(activeUser);
  }

  get vehicleManagementVisibility() {
    const activeUser = this.effectiveUser;

    return vehicleManagementAccess(activeUser);
  }

  get roleManagementVisibility() {
    const activeUser = this.effectiveUser;

    return roleManagementAccess(activeUser);
  }

  get isRoleEditable() {
    return defineOwnRoleSettingsClientAccess(true);
  }

  get administrationSettingsVisibility() {
    const activeUser = this.effectiveUser;

    return viewAdminsitrationSettingsAccess(activeUser);
  }

  get userManagementSection(): SettingData {
    return {
      title: this.$t("settings.userManagement").toString(),
      items: [
        {
          title: this.$t("settings.users").toString(),
          path: "userManagement",
          icon: "mdi-account-group",
          visible: true,
        },
        {
          title: this.$t("settings.userSettings.title").toString(),
          path: "userSettings",
          icon: "mdi-account-cog",
          visible: this.userSettingsVisibility,
        },
        {
          title: this.$t("settings.administration.userSettings.title").toString(),
          path: "userSettingsAdministration",
          visible: this.administrationSettingsVisibility,
        },
        {
          title: this.$t(this.isRoleEditable ? "settings.roleManagement.title" : "settings.roleManagement.titleAlt").toString(),
          path: "roleManagement",
          icon: this.isRoleEditable ? "mdi-pencil" : "mdi-account-badge",
          visible: this.roleManagementVisibility,
        },
        {
          title: this.$t("settings.administration.roleSettings.title").toString(),
          path: "roleSettingsAdministration",
          visible: false,
        },
        {
          title: this.$t("settings.vehicleManagement.title").toString(),
          path: "vehicleManagement",
          icon: "mdi-car-cog",
          visible: this.vehicleManagementVisibility,
        },
      ],
    };
  }

  get branchesVisibility() {
    return branchManagementAccess(); 
  }

  get companySettingsSection(): SettingData {
    return {
      title: this.$t("settings.company").toString(),
      items: [
        {
          title: this.$t("settings.companySettings.companyData").toString(),
          path: "companyData",
          icon: "mdi-office-building",
          visible: true,
        },
        {
          title: this.$t("settings.branches.title").toString(),
          path: "branches",
          icon: "mdi-map-marker",
          visible: this.branchesVisibility,
        },
        {
          title: this.$t("settings.companySettings.companySettingsTitle").toString(),
          path: "companySettings",
          icon: "mdi-office-building-cog",
          visible: true,
        },
        {
          title: this.$t("settings.administration.companySettings.title").toString(),
          path: "companySettingsAdministration",
          visible: this.administrationSettingsVisibility,
        },
      ],
    };
  }

  get reportSettingsSection(): SettingData {
    return {
      title: this.$t("settings.report.title").toString(),
      items: [
        {
          title: this.$t("settings.report.reportSettings").toString(),
          path: "reportSettings",
          icon: "mdi-file-cog",
          visible: true,
        },
        {
          title: this.$t("settings.administration.reportSettings.title").toString(),
          path: "reportSettingsAdministration",
          visible: this.administrationSettingsVisibility,
        },
      ],
    };
  }

  get hyDryDeviceSettingsSection(): SettingData {
    return {
      title: this.$t("settings.hyDryDevice.title").toString(),
      items: [
        {
          title: this.$t("settings.hyDryDevice.configurations").toString(),
          path: "hyDryDeviceSettings",
          visible: this.showHyDryDeviceSettings,
        },
        {
          title: this.$t("settings.administration.hyDryDeviceSettings.title").toString(),
          path: "hyDryDeviceSettingsAdministration",
          visible: this.administrationSettingsVisibility,
        },
      ],
    };
  }

  get deviceSettingsVisbility() {
    const activeUser = this.effectiveUser;

    return deviceSettingsItemAccess(activeUser);
  }

  get importDevicesVisibility() {
    const activeUser = this.effectiveUser;

    return importDevicesAccess(activeUser);
  }

  get otherDeviceSettingsSections(): SettingData {
    return {
      title: this.$t("settings.allDevices.title").toString(),
      items: [
        {
          title: this.$t("settings.hyDryDevice.devicesCategorySettings").toString(),
          path: "deviceCategories",
          icon: "mdi-view-dashboard",
          visible: this.deviceSettingsVisbility,
        },
        {
          title: this.$t("settings.allDevices.projectDevicesSettings.title").toString(),
          path: "projectDevicesSettings",
          visible: this.deviceSettingsVisbility,
        },
        {
          title: this.$t("settings.hyDryDevice.configurationsImportExport").toString(),
          path: "deviceSettings",
          icon: "mdi-cog",
          visible: this.importDevicesVisibility,
        },
        {
          title: this.$t("settings.hyDryDevice.devicesImport").toString(),
          path: "deviceImport",
          icon: "mdi-file-import",
          visible: this.importDevicesVisibility,
        },
        {
          title: this.$t("settings.hyDryDevice.devicesExport").toString(),
          path: "deviceExport",
          icon: "mdi-file-export",
          visible: this.importDevicesVisibility,
        },
        {
          title: this.$t("settings.hyDryDevice.deviceImportHistory").toString(),
          path: "deviceImportHistory",
          icon: "mdi-history",
          visible: this.importDevicesVisibility,
        },
        {
          title: this.$t("settings.administration.allDevicesSettings.title").toString(),
          path: "allDevicesSettingsAdministration",
          visible: this.administrationSettingsVisibility,
        },
      ]
    };
  }

  get messageSelectionVisibility() {
    const activeUser = this.effectiveUser;

    return messageSelectionAccess(activeUser);
  }

  get mailboxVisibility() {
    const activeUser = this.effectiveUser;

    return mailboxAccess(activeUser);
  }

  get messageSettingsSection(): SettingData {
    return {
      title: this.$t("settings.messages").toString(),
      items: [
        {
          title: this.$t("settings.messageSelection").toString(),
          path: "messageSelection",
          icon: "mdi-message-alert",
          visible: this.messageSelectionVisibility || this.administrationSettingsVisibility,
        },
        {
          title: this.$t("settings.mailboxes").toString(),
          path: "mailbox",
          icon: "mdi-mailbox",
          visible: this.mailboxVisibility || this.administrationSettingsVisibility,
        },
        {
          title: this.$t("settings.administration.hyDryMessagesSettings.title").toString(),
          path: "hyDryMessagesSettingsAdministration",
          visible: this.administrationSettingsVisibility,
        },
      ],
    };
  }

  get projectManagerSettingsVisibility() {
    const projectManagementType = getProjectManagementType();

    return this.hasProjectManagementRouteAccess && (
      projectManagementType === ProjectManagement.PROJECT_OPTION ||
      projectManagementType === ProjectManagement.DEFAULT ||
      defineProjectManagementSettingsForGroupClientAccess()
    );
  }

  get projectSettingsVisibility() {
    return this.hasProjectManagementRouteAccess;
  }

  get deviceManagerSettingsVisibility() {
    const activeUser = this.effectiveUser;

    return this.hasProjectManagementRouteAccess &&
      deviceManagerSettingsAccess(activeUser);
  }

  get archivedProjectsVisibility() {
    return viewArchivedProjectSettingsAccess(this.effectiveUser);
  }

  get projectManagementSection(): SettingData {
    return {
      title: this.$t("settings.projectManagement.title").toString(),
      items: [
        {
          title: this.$t("settings.projectManagement.projectEditingSettings.title").toString(),
          path: "projectSettings",
          visible: this.projectSettingsVisibility,
        },
        {
          title: this.$t("settings.projectManagement.projectManagerSettings.title").toString(),
          path: "projectManagerSettings",
          visible: this.projectManagerSettingsVisibility,
        },
        {
          title: this.$t("settings.projectManagement.deviceManagerSettings.title").toString(),
          path: "deviceManagerSettings",
          visible: this.deviceManagerSettingsVisibility,
        },
        {
          title: this.$t("settings.projectManagement.archivedProjects.title").toString(),
          path: "/main/archivedProjects",
          icon: "mdi-archive",
          visible: this.archivedProjectsVisibility,
        },
        {
          title: this.$t("settings.administration.projectManagementSettings.title").toString(),
          path: "projectManagementSettingsAdministration",
          visible: this.administrationSettingsVisibility,
        },
      ],
    };
  }

  get systemSettingsVisibility() {
    const activeUser = this.effectiveUser;

    return systemSettingsAccess(activeUser);
  }

  get systemConfigurationVisibility() {
    const activeUser = this.effectiveUser;

    return systemConfigurationAccess(activeUser);
  }

  get finishedProjectsSettingsVisibility() {
    const activeUser = this.effectiveUser;

    // Hidden for the admin portal even though the rest of the System section stays: it configures
    // how finished projects behave, and that client has none.
    return !this.hidesClientBusinessSettings && viewFinishedProjectsSettingsAccess(activeUser);
  }

  get systemConfigurationDistributionVisibility() {
    const activeUser = this.effectiveUser;

    return viewSystemConfigurationDistributionAccess(activeUser);
  }

  get systemSettingsSection(): SettingData {
    return {
      title: this.$t("settings.system.title").toString(),
      items: [
        {
          title: this.$t("settings.system.clientEmailAndPassword").toString(),
          onClickCallback: async () => {
            //Not working with @Ref("changeEmailAndPassword"), no idea why
            const changeEmailAndPasswordComponent = this.$refs
              .changeEmailAndPassword as ChangeEmailAndPasswordComponent;

            if (changeEmailAndPasswordComponent) {
              await changeEmailAndPasswordComponent.show(false);
            }
          },
          visible: true,
        },
        {
          title: this.$t("settings.system.finishedProjects.title").toString(),
          path: "finishedProjects",
          visible: this.finishedProjectsSettingsVisibility,
        },
        {
          title: this.$t("settings.system.dataStorage").toString(),
          path: "systemSettings",
          visible: this.systemSettingsVisibility,
        },
        {
          title: this.$t("settings.system.systemConfiguration").toString(),
          path: "systemConfiguration",
          visible: this.systemConfigurationVisibility,
        },
        {
          title: this.$t("settings.administration.systemConfiguration.title").toString(),
          path: "systemConfigurationAdministration",
          visible: this.systemConfigurationDistributionVisibility,
        },
      ],
    };
  }

  get timeTrackingSettingsSection(): SettingData {
    return {
      title: this.$t("settings.timeTracking.sectionTitle").toString(),
      items: [
        {
          title: this.$t("settings.timeTracking.workingHours").toString(),
          path: "workingHours",
          visible: true,
        },
        {
          title: this.$t("settings.timeTracking.configuration").toString(),
          path: "timeTrackingConfigurations",
          visible: true,
        },
        {
          title: this.$t("settings.timeTracking.dailyTracking").toString(),
          path: "dailyTrackingSettings",
          visible: true,
        },
      ],
    };
  }

  get schedulingSettingsSection(): SettingData {
    return {
      title: this.$t("settings.scheduling.title").toString(),
      items: [
        SchedulingSettingsSection.PLANNING_BOARD,
        SchedulingSettingsSection.GEOCODING_AND_TRAVEL_TIMES,
        SchedulingSettingsSection.PLANNING_CONTROL,
      ].map((section) => {
        return {
          title: localiseSchedulingSettingsSection(this, section).toString(),
          icon: "mdi-view-dashboard",
          onClickCallback: () => {
            router.push({
              path: "schedulingSettings",
              query: {
                section: section,
              },
              append: true,
            });
          },
          visible: true,
        };
      }),
    };
  }

  get administratorSettingsSection(): SettingData {
    return {
      title: this.$t("settings.administrator.title").toString(),
      items: [
        {
          title: this.$t("settings.administrator.memberClientsManagement.title").toString(),
          path: "memberClientsManagement",
          icon: "mdi-domain",
          visible: this.isAllowedToManageMemberClients,
        },
        /* {
          title: this.$t("settings.administrator.memberClientDialog.title").toString(),
          icon: "mdi-domain-plus",
          visible: this.isAllowedToCreateMemberClient,
          onClickCallback: () => {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            this.memberClientDialog.show();
          },
        }, */
      ]
    };
  }

  get iresSettingsSection(): SettingData {
    return {
      title: this.$t("settings.ires.title").toString(),
      items: [
        {
          title: this.$t("settings.ires.iresConfiguration").toString(),
          path: "iresConfiguration",
          visible: true,
        },
        // Mandant Test-App moved to the Test card of the client in the IRES Admin Client (#2610070),
        // MASTER Einstellungen to its Gruppe card (#2610074), and Hilfsfunktionen (Alle Trocknungen neu
        // berechnen) to the client detail's Hilfsfunktionen card (#2610077).
      ]
    };
  }

  get billingCatalogSection(): SettingData {
    return {
      title: this.$t("settings.billingCatalogs.sectionTitle").toString(),
      items: [
        {
          title: this.$t("settings.billingCatalogs.listTitle").toString(),
          path: "billingCatalogs",
          icon: "mdi-book-open-variant",
          visible: true,
        },
      ],
    };
  }
}
